/**
 * 权益卡片埋点工具
 * 提供统一的埋点上报方法
 */
import { ubtButtonNew, ubtModuleExposeNew } from '@/shared/utils/ubt';
import type { RightsContext } from '../types';

/**
 * 埋点事件类型
 */
export enum TrackingEventType {
  /** 模块曝光 */
  EXPOSE = 'EXPOSE',
  /** 按钮点击 */
  CLICK = 'CLICK',
}

/**
 * 埋点场景枚举
 */
export enum TrackingScene {
  /** 权益卡片曝光 */
  CARD_EXPOSE = 'finance_loan_apply_right_expose',
  /** 切换到极速通道 */
  TAB_SPEED_CLICK = 'fincance_inloan_speedapply_click',
  /** 切换到普通通道 */
  TAB_NORMAL_CLICK = 'fincance_inloan_normalapply_click',
  /** 查看详情点击 */
  DETAIL_CLICK = 'fincance_inloan_detail_click',
  /** 详情关闭 */
  DETAIL_CLOSE = 'fincance_inloan_detail_close',
  /** 勾选协议 */
  AGREEMENT_CHECK = 'finance_loan_apply_right_agreementCheck',
  /** 查看协议 */
  AGREEMENT_VIEW = 'finance_loan_apply_right_agreement',
}

/**
 * 生成埋点通用参数
 * 根据当前权益上下文生成埋点所需的业务参数
 */
export const generateTrackingParams = (context: Partial<RightsContext>): Dictionary => {
  const {
    channel,
    amount,
    period,
    showSpeed,
    rightSummaryData,
    planListData,
    capitalCode,
    from,
  } = context;

  return {
    // 产品信息
    productId: channel?.productId || '',
    capitalCode: capitalCode || '',
    from: from || '',
    // sourceId: (channel as quickLoanChannelType)?.sourceId || '',

    // 订单信息
    amount: amount || '',
    period: period || '',

    // 权益信息
    packageId: rightSummaryData?.packageId || '',
    packageType: rightSummaryData?.packageType || '',
    sellPrice: rightSummaryData?.sellPrice || '',
    saveMoney: rightSummaryData?.saveMoney || '',

    // 状态信息
    showSpeed: showSpeed ? 1 : 0,

    // 利率信息
    rateType: planListData?.rateType || '',
    rateValue: planListData?.rateValue || '',
    pricingType: planListData?.pricingType || '',
  };
};

/**
 * 发送埋点数据
 * @param pageId 页面ID
 * @param scene 埋点场景
 * @param eventType 事件类型
 * @param extraParams 额外参数
 */
export const sendTracking = (
  pageId: string,
  scene: TrackingScene,
  eventType: TrackingEventType,
  extraParams: Dictionary = {},
): void => {
  if (eventType === TrackingEventType.EXPOSE) {
    ubtModuleExposeNew(pageId, scene, extraParams);
  } else {
    ubtButtonNew(pageId, scene, extraParams);
  }
};

/**
 * 埋点管理器接口
 */
export interface TrackingManager {
  /** 卡片曝光埋点 */
  trackCardExpose: (context: Partial<RightsContext>) => void;
  /** Tab切换埋点 */
  trackTabSwitch: (context: Partial<RightsContext>, toSpeed: boolean) => void;
  /** 查看详情埋点 */
  trackDetailClick: (context: Partial<RightsContext>) => void;
  /** 详情关闭埋点 */
  trackDetailClose: (context: Partial<RightsContext>) => void;
  /** 协议勾选埋点 */
  trackAgreementCheck: (context: Partial<RightsContext>, checked: boolean) => void;
  /** 查看协议埋点 */
  trackAgreementView: (context: Partial<RightsContext>) => void;
}

/**
 * 创建埋点管理器
 * 提供基于 RightsContext 的便捷埋点方法
 */
export const createTrackingManager = (pageId: string): TrackingManager => {
  /**
   * 卡片曝光埋点
   */
  const trackCardExpose = (context: Partial<RightsContext>): void => {
    const params = generateTrackingParams(context);
    sendTracking(pageId, TrackingScene.CARD_EXPOSE, TrackingEventType.EXPOSE, params);
  };

  /**
   * Tab切换埋点
   */
  const trackTabSwitch = (context: Partial<RightsContext>, toSpeed: boolean): void => {
    const params = generateTrackingParams(context);
    const scene = toSpeed ? TrackingScene.TAB_SPEED_CLICK : TrackingScene.TAB_NORMAL_CLICK;
    sendTracking(pageId, scene, TrackingEventType.CLICK, params);
  };

  /**
   * 查看详情埋点
   */
  const trackDetailClick = (context: Partial<RightsContext>): void => {
    const params = generateTrackingParams(context);
    sendTracking(pageId, TrackingScene.DETAIL_CLICK, TrackingEventType.CLICK, params);
  };

  /**
   * 详情关闭埋点
   */
  const trackDetailClose = (context: Partial<RightsContext>): void => {
    const params = generateTrackingParams(context);
    sendTracking(pageId, TrackingScene.DETAIL_CLOSE, TrackingEventType.CLICK, params);
  };

  /**
   * 协议勾选埋点
   */
  const trackAgreementCheck = (context: Partial<RightsContext>, checked: boolean): void => {
    const params = {
      ...generateTrackingParams(context),
      checked: checked ? 1 : 0,
    };
    sendTracking(pageId, TrackingScene.AGREEMENT_CHECK, TrackingEventType.CLICK, params);
  };

  /**
   * 查看协议埋点
   */
  const trackAgreementView = (context: Partial<RightsContext>): void => {
    const params = generateTrackingParams(context);
    sendTracking(pageId, TrackingScene.AGREEMENT_VIEW, TrackingEventType.CLICK, params);
  };

  return {
    trackCardExpose,
    trackTabSwitch,
    trackDetailClick,
    trackDetailClose,
    trackAgreementCheck,
    trackAgreementView,
  };
};
